// Mensagens prontas de WhatsApp que o admin dispara com um clique pra casais
// parados no funil (aba "Ativação e engajamento" do painel admin). Nada é
// enviado automaticamente: gera um link wa.me com o texto pré-preenchido e o
// admin confere/envia no próprio WhatsApp.

import { parseLocalDate, phoneDigits } from './utils';

const SITE = 'https://mimodocasal.com.br';

// Normaliza telefone BR pra dígitos com DDI (55). Aceita com ou sem máscara
// e com ou sem 55 na frente. Retorna '' se não parecer um número válido
// (10 dígitos = fixo, 11 = celular com 9 na frente; DDD 11–99).
export function normalizeBrPhone(raw) {
  let d = phoneDigits(raw);
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  if (d.length !== 10 && d.length !== 11) return '';
  if (Number(d.slice(0, 2)) < 11) return '';
  if (d.length === 11 && d[2] !== '9') return '';
  return `55${d}`;
}

// Primeiro nome com só a inicial maiúscula (cadastros vêm em CAIXA ALTA ou
// minúsculo). Mantém hífen e apóstrofo: "ANA-MARIA" vira "Ana-Maria".
export function firstName(fullName) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';
  return first.toLocaleLowerCase('pt-BR').replace(/(^|[-'])(\p{L})/gu, (_, sep, ch) => sep + ch.toLocaleUpperCase('pt-BR'));
}

const hello = (name) => `Oi${name ? `, ${name}` : ''}! Tudo bem? Aqui é do Mimo do Casal.\n\n`;

// Um modelo por situação (segmento) da aba "Ativação e engajamento". `build`
// recebe o primeiro nome e, quando faz sentido, um contexto ({ daysToWedding }).
export const OUTREACH_TEMPLATES = {
  onboarding: {
    label: 'Terminar o cadastro',
    build: (name) =>
      hello(name) +
      `Vi que você criou sua conta mas ainda não terminou de montar a página de vocês. ` +
      `Leva só uns minutinhos: é só entrar em ${SITE}/onboarding, preencher os dados do casamento e pronto, a lista já fica no ar.\n\n` +
      `Se travou em algum passo, me responde aqui que eu te ajudo!`,
  },
  noGifts: {
    label: 'Criar os presentes',
    build: (name) =>
      hello(name) +
      `Vi que você já criou a conta e a página de vocês está no ar, mas a lista de presentes ainda está vazia. ` +
      `Dá pra resolver em um minutinho: entra em ${SITE}/dashboard/cards e escolhe alguns presentes da nossa biblioteca de sugestões prontas (lua de mel, casa nova, jantar romântico...).\n\n` +
      `Qualquer dúvida é só responder aqui, tá?`,
  },
  nearNoGifts: {
    label: 'Casamento chegando, sem presentes',
    build: (name, ctx = {}) => {
      const d = ctx.daysToWedding;
      const when = Number.isFinite(d) ? (d <= 1 ? 'é amanhã' : `é daqui a ${d} dias`) : 'está chegando';
      return (
        hello(name) +
        `Vi que você criou a conta e o casamento de vocês ${when}, mas a lista de presentes ainda não tem nenhum item. ` +
        `Quanto antes ela entrar no ar, mais tempo os convidados têm pra presentear. Entra em ${SITE}/dashboard/cards, escolhe alguns presentes das sugestões prontas e compartilha o link com a família.\n\n` +
        `Se quiser, me fala aqui que eu te ajudo a montar agora!`
      );
    },
  },
  giftsCheck: {
    label: 'Ver se está tudo certo',
    build: (name) =>
      hello(name) +
      `Vi que você já criou a conta e cadastrou os presentes da lista de vocês, ficou lindo! ` +
      `Queria saber se está tudo certo por aí e se você precisa de ajuda com alguma coisa (ajustar valores, fotos, configurar o recebimento ou compartilhar o link com os convidados).\n\n` +
      `É só me responder aqui, tá?`,
  },
  noRecipient: {
    label: 'Configurar o recebimento',
    build: (name) =>
      hello(name) +
      `Vi que você criou a conta e a lista de presentes de vocês já está pronta, ficou ótima! ` +
      `Antes de divulgar o link pros convidados, falta só configurar o recebimento, que é onde vocês cadastram a chave Pix pra o dinheiro dos presentes cair na conta de vocês. ` +
      `Leva uns minutinhos: entra em ${SITE}/dashboard/settings?tab=payment, preenche os dados e pronto. Depois disso é só compartilhar a página com a família e os amigos.\n\n` +
      `Se travar em algum passo, me responde aqui que eu te ajudo!`,
  },
  noContrib: {
    label: 'Ajudar a divulgar a lista',
    build: (name) =>
      hello(name) +
      `Vi que você criou a conta e a lista de presentes de vocês já está pronta, mas ainda não chegou nenhum presente. ` +
      `Normalmente isso muda quando o link começa a circular: manda a página pro grupo da família e dos amigos (dá pra copiar o link e compartilhar pelo WhatsApp direto no painel) e deixa o link na bio ou nos convites.\n\n` +
      `Se quiser ajuda com algum passo, me responde aqui!`,
  },
};

// Status de contato por casal (coleção adminOutreach/{uid}).
export const OUTREACH_STATUSES = {
  none: { label: 'Nenhum contato', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  sent: { label: 'Mensagem enviada', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  replied: { label: 'Respondeu', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved: { label: 'Resolvido', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

// Janela (dias) pra considerar o casamento "próximo", e quantos dias de conta
// separam "acabou de cadastrar os presentes" de "já deu tempo de divulgar".
export const NEAR_WEDDING_DAYS = 45;
export const GIFTS_CHECK_MAX_DAYS = 7;
// Divulgar a lista (ou configurar o recebimento pra poder divulgar) só faz
// sentido quando o casamento está perto: ~6 meses. Casamento sem data entra.
export const SHARE_WINDOW_DAYS = 180;

const toMs = (v) => (v?.seconds ? v.seconds * 1000 : typeof v === 'number' ? v : 0);

// Recebedor configurado = subconta Asaas criada (asaas.walletId). Só a chave
// Pix salva ainda não basta: sem subconta o pagamento dos convidados não sai.
export const hasRecipient = (wedding) => Boolean(wedding?.asaas?.walletId);

// Classifica uma conta em exatamente uma situação (ou null se não precisa de
// contato). Prioridade: sem onboarding > casamento próximo sem presentes >
// sem presentes > presentes sem nenhuma contribuição (recente = conferir;
// antigo e casamento em até ~6 meses = divulgar, ou configurar o recebimento antes se ainda não há recebedor). Quem já recebeu presente ou cujo casamento já passou
// fica de fora.
export function classifyOutreach({ wedding, createdAtMs, now = Date.now() }) {
  if (!wedding) return { kind: 'onboarding' };
  const contribs = Array.isArray(wedding.contributions) ? wedding.contributions : [];
  if (contribs.some((c) => !c.status || c.status === 'paid')) return null;

  let daysToWedding = null;
  if (wedding.weddingDate) {
    const d = parseLocalDate(wedding.weddingDate);
    if (!Number.isNaN(d.getTime())) {
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      daysToWedding = Math.floor((d - today) / 86400000); // d é meio-dia local; floor dá a diferença exata em dias
      if (daysToWedding < 0) return null;
    }
  }

  const cards = Array.isArray(wedding.cards) ? wedding.cards.length : 0;
  if (cards === 0) {
    if (daysToWedding !== null && daysToWedding <= NEAR_WEDDING_DAYS) return { kind: 'nearNoGifts', daysToWedding };
    return { kind: 'noGifts', daysToWedding };
  }
  const accountDays = createdAtMs ? (now - createdAtMs) / 86400000 : Infinity;
  if (accountDays <= GIFTS_CHECK_MAX_DAYS) return { kind: 'giftsCheck', daysToWedding };
  if (daysToWedding !== null && daysToWedding > SHARE_WINDOW_DAYS) return null;
  // Divulgar a lista sem recebedor configurado não adianta: primeiro orientar
  // a configurar o recebimento.
  return { kind: hasRecipient(wedding) ? 'noContrib' : 'noRecipient', daysToWedding };
}

// Monta a lista da aba a partir de users/{uid} e weddings/{uid}. Contas
// excluídas ou de teste ficam de fora.
export function buildOutreachEntries({ usersByUid, weddings, now = Date.now() }) {
  const byId = new Map(weddings.map((w) => [w.id, w]));
  const entries = [];
  Object.entries(usersByUid).forEach(([uid, u]) => {
    if (u.deleted || u.isTest) return;
    const w = byId.get(uid) || null;
    const createdAtMs = toMs(w?.createdAt) || toMs(u.createdAt);
    const seg = classifyOutreach({ wedding: w, createdAtMs, now });
    if (!seg) return;
    const phone = w?.payout?.phone || w?.phone || u.phone || '';
    entries.push({
      uid,
      kind: seg.kind,
      daysToWedding: seg.daysToWedding ?? null,
      weddingDate: w?.weddingDate || '',
      hasWedding: Boolean(w),
      name: firstName(u.name) || firstName(w?.brideName || w?.groomName),
      couple: w ? [w.groomName, w.brideName].filter(Boolean).join(' & ') : '',
      fullName: u.name || '',
      email: u.email || '',
      slug: w?.slug || '',
      published: w?.isPublished === true,
      phone,
      hasPhone: normalizeBrPhone(phone) !== '',
      createdAtMs,
      days: createdAtMs ? Math.floor((now - createdAtMs) / 86400000) : null,
    });
  });
  return entries.sort((a, b) => b.createdAtMs - a.createdAtMs);
}

// "faltam 12 dias" / "é amanhã" / "é hoje" a partir de daysToWedding.
export function daysToWeddingLabel(days) {
  if (!Number.isFinite(days)) return '';
  if (days === 0) return 'é hoje';
  if (days === 1) return 'é amanhã';
  return `faltam ${days} dias`;
}

// Link público da página do casamento (vazio se ainda não há slug).
export function weddingUrl(slug) {
  return slug ? `${SITE}/${slug}` : '';
}

export function buildWhatsappLink(phone, text) {
  const n = normalizeBrPhone(phone);
  if (!n) return '';
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
}
